<template>
  <with-actions>
    <div class="card" v-if="plan">
      <div class="card-body">
        <h4>{{ 'plan' | t }}</h4>
        <hr />
        <div class="row">
          <div class="col-md-4">
            <h3>{{ 'basic' | t }}</h3>
            <p class="text-muted">{{ 'basic_plan_information' | t }}</p>
          </div>
          <div class="col-md-8">
            <div class="form-group" v-if="can('read_organizations')">
              <label class="mb-0">{{ 'organization' | t }}</label>
              <template v-if="plan.id">
                <div>{{ plan.organization.name }}</div>
              </template>
              <template v-else>
                <select class="form-control" v-model="plan.organization">
                  <option :value="null">---</option>
                  <option v-for="organization in organizations" :value="organization">{{ organization.name }}</option>
                </select>
              </template>
            </div>
            <div class="form-group">
              <label class="mb-0">{{ 'name' | t }}</label>
              <input class="form-control" v-model="plan.name" />
            </div>
            <template v-if="!is_whitelabel">
              <div class="form-group">
                <label class="mb-0">{{ 'plan_group' | t }}</label>
                <input class="form-control" v-model="plan.plan_group" />
              </div>
              <div class="form-group">
                <label class="mb-0">{{ 'category' | t }}</label>
                <select class="form-control" v-model="plan.category">
                  <option :value="nil"></option>
                  <option value="license">License</option>
                  <option value="consumption_shipments">Consumption shipments</option>
                  <option value="products">Products</option>
                  <option value="consultancy">Consultancy</option>
                  <option value="license_white_label" v-if="!is_whitelabel">License White Label</option>
                  <option value="support_license_white_label" v-if="!is_whitelabel">Support License White Label</option>
                </select>
              </div>
              <div class="form-group">
                <label class="mb-0">{{ 'tier' | t }}</label>
                <input class="form-control" v-model="plan.tier" />
              </div>
              <div class="form-group">
                <label class="mb-0">{{ 'generation' | t }}</label>
                <input class="form-control" v-model="plan.generation" />
              </div>
              <div class="form-group">
                <label class="mb-0">{{ 'visible' | t }}</label>
                <input class="ml-4" type="checkbox" v-model="plan.visible" />
              </div>
            </template>
          </div>
        </div>
        <hr v-if="!is_whitelabel" />
        <div class="row" v-if="!is_whitelabel">
          <div class="col-md-4">
            <h3>{{ 'billing' | t }}</h3>
            <p class="text-muted">{{ 'billing_plan_information' | t }}</p>
          </div>
          <div class="col-md-8">
            <div class="form-group">
              <label class="mb-0">{{ 'currency' | t }}</label>
              <select class="form-control" v-model="plan.currency">
                <option value="DKK">{{ 'DKK' }}</option>
                <option value="EUR">{{ 'EUR' }}</option>
                <option value="SEK">{{ 'SEK' }}</option>
                <option value="NOK">{{ 'NOK' }}</option>
              </select>
            </div>
            <div class="form-group">
              <label class="mb-0">{{ 'billing_period' | t }}</label>
              <select class="form-control" v-model="plan.billing_period">
                <option value="monthly">{{ 'monthly' | t }}</option>
                <option value="quarterly">{{ 'quarterly' | t }}</option>
                <option value="yearly">{{ 'yearly' | t }}</option>
              </select>
            </div>
            <div class="form-group">
              <label class="mb-0">{{ 'payment_fee_percentage' | t }}</label>
              <div class="input-group">
                <input class="form-control" v-model="plan.payment_fee_percentage" />
                <div class="input-group-append">
                  <label class="input-group-text">{{ '%' }}</label>
                </div>
              </div>
            </div>
            <div class="form-group">
              <label class="mb-0">{{ 'bank_payment_fee' | t }}</label>
              <div class="input-group">
                <input class="form-control" v-model="plan.bank_payment_fee" />
                <div class="input-group-append">
                  <label class="input-group-text">{{ plan.currency }}</label>
                </div>
              </div>
            </div>
            <div class="form-group">
              <label class="mb-0">{{ 'billing_product_id' | t }}</label>
              <input class="form-control" :disabled="plan.id" v-model="plan.billing_product_id" />
            </div>
            <div class="form-group">
              <label class="mb-0">{{ 'product_for_crediting' | t }}</label>
              <input class="ml-4" v-model="product_for_crediting" type="checkbox" />
            </div>
          </div>
        </div>
        <hr />
        <div class="row mt-4">
          <div class="col-md-4">
            <h3>{{ 'included_resources' | t }}</h3>
            <p class="text-muted">{{ 'included_resources_plan_information' | t }}</p>
          </div>
          <div class="col-md-8">
            <template v-if="is_whitelabel">
              <div class="alert alert-info">{{ 'whitelabel_plan_notice' | t }}</div>
              <div class="form-group">
                <label class="mb-0">{{ 'minimum_shipments' | t }}</label>
                <input class="form-control" v-model="plan.minimum_shipments" />
              </div>
              <div class="form-group">
                <label class="mb-0">{{ 'minimum_orders' | t }}</label>
                <input class="form-control" v-model="plan.minimum_orders" />
              </div>
            </template>
            <hr />
            <div class="form-group" v-for="resource in resources">
              <label class="mb-0">{{ `included_${resource}` | t }}</label>
              <input class="form-control" v-model="plan[`included_${resource}`]" />
            </div>
          </div>
        </div>
        <hr />
        <div class="row mt-4">
          <div class="col-md-4">
            <h3>{{ 'pricing' | t }}</h3>
            <p class="text-muted">{{ 'pricing_plan_information' | t }}</p>
          </div>
          <div class="col-md-8">
            <div class="form-group" v-if="!is_whitelabel">
              <label class="mb-0">{{ 'recurring_price' | t }}</label>
              <div class="input-group">
                <input class="form-control" v-model="plan.price" />
                <div class="input-group-append">
                  <label class="input-group-text">{{ plan.currency }}</label>
                </div>
              </div>
            </div>
            <div class="form-group" v-for="resource in [...resources, 'locked_shipments']">
              <label class="mb-0">{{ `price_${resource}` | t }}</label>
              <div class="input-group">
                <input class="form-control" v-model="plan[`price_${resource}`]" />
                <div class="input-group-append">
                  <label class="input-group-text">{{ plan.currency }}</label>
                </div>
              </div>
              <div class="input-group mt-1" v-if="resource === 'orders'">
                <input
                  class="mr-2"
                  type="checkbox"
                  :disabled="!plan[`price_${resource}`] || plan[`price_${resource}`] &lt;= 0"
                  v-model="plan.include_deleted_orders"
                />
                <label>{{ 'include_deleted_orders' | t }}</label>
              </div>
            </div>
          </div>
        </div>
        <template v-if="!is_whitelabel">
          <hr />
          <div class="row mt-4">
            <div class="col-md-4">
              <h3>{{ 'support' | t }}</h3>
              <p class="text-muted">{{ 'support_plan_information' | t }}</p>
            </div>
            <div class="col-md-8">
              <div class="form-group">
                <label class="mb-0">{{ 'default_support_class' | t }}</label>
                <select class="form-control" v-model="plan.support_class">
                  <option value="level_one">{{ 'level_one_support_plan_name' | t }}</option>
                  <option value="level_two">{{ 'level_two_support_plan_name' | t }}</option>
                  <option value="level_three">{{ 'level_three_support_plan_name' | t }}</option>
                </select>
              </div>
              <div class="form-group">
                <label class="mb-0">{{ 'level_one_support_price' | t }}</label>
                <div class="input-group">
                  <input class="form-control" v-model="plan.level_one_support_price" />
                  <div class="input-group-append">
                    <label class="input-group-text">{{ plan.currency }}</label>
                  </div>
                </div>
              </div>
              <div class="form-group">
                <label class="mb-0">{{ 'level_two_support_price' | t }}</label>
                <div class="input-group">
                  <input class="form-control" v-model="plan.level_two_support_price" />
                  <div class="input-group-append">
                    <label class="input-group-text">{{ plan.currency }}</label>
                  </div>
                </div>
              </div>
              <div class="form-group">
                <label class="mb-0">{{ 'level_three_support_price' | t }}</label>
                <div class="input-group">
                  <input class="form-control" v-model="plan.level_three_support_price" />
                  <div class="input-group-append">
                    <label class="input-group-text">{{ plan.currency }}</label>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <hr />
        </template>
        <div class="row mt-4">
          <div class="col-md-4">
            <h3>{{ 'available_features' | t }}</h3>
            <p class="text-muted">{{ 'available_features_description' | t }}</p>
          </div>
          <div class="col-md-8">
            <plans-features-table v-model="enabledFeatures" :scopes="scopes"></plans-features-table>
          </div>
        </div>
        <div class="row text-right">
          <div class="col-12">
            <hr />
          </div>
          <div class="col-12">
            <button class="btn btn-success" @click="save">{{ id ? 'update' : 'create' | t }}</button>
          </div>
        </div>
      </div>
    </div>
  </with-actions>
</template>
<script>
import Api from '@/store/jsonapi';
import config from '@/config';
import _ from 'lodash';

export default {
  props: ['plan_id'],
  data: function () {
    return {
      plan: null,
      id: this.plan_id,
      scopes: [
        'order_management',
        'orders',
        'basic_mail_templates',
        'advanced_mail_templates',
        'basic_slip_templates',
        'advanced_slip_templates',
        'stores',
        'shipment_management',
        'shipments',
        'printers',
        'waybills',
        'pickups',
        'document_library',
        'return_management',
        'returns',
        'cost_management',
        'cost_sheets',
        'basic_account_management',
        'advanced_account_management',
        'api_access',
        'automations',
        'webhooks',
        'csv_mappings',
        'reports',
        'access_tokens',
        'brands'
      ],
      enabledFeatures: [],
      organizations: [],
      is_whitelabel: false,

      resources: [
        'users',
        'shipments',
        'orders',
        'carriers',
        'order_channels',
        'printers',
        'automations',
        'return_portals'
      ]
    };
  },
  computed: {
    // Add or removed "Crediting " prefix on plan name used to indicate if the plan should be used when creating credit memos
    product_for_crediting: {
      get() {
        if (!this.plan) {
          return false;
        }
        return _.startsWith(this.plan.name, 'Crediting ');
      },
      set(v) {
        if (v) {
          if (!_.startsWith(this.plan.name, 'Crediting ')) {
            this.plan.name = 'Crediting ' + this.plan.name;
          }
        } else {
          this.plan.name = _.replace(this.plan.name, 'Crediting ', '');
        }
      }
    }
  },
  methods: {
    save() {
      this.plan.feature_scopes = this.enabledFeatures.reduce((accString, feature) => {
        let featureString = feature;

        if (accString.length > 0) {
          return accString + ' ' + featureString;
        } else {
          return featureString;
        }
      }, '');

      if (this.plan_id) {
        Api.update('plans', _.omit(this.plan, 'subscriptions', 'organization'))
          .then(({ data }) => {
            Alerts.success({ title: 'Plan was updated', detail: '' });
          })
          .catch((errors) => {
            _.forEach(errors, (error) => {
              Alerts.danger(error[0]);
            });
          });
      } else {
        Api.create('plans', this.plan)
          .then(({ data }) => {
            Alerts.success({ title: 'Plan was created', detail: '' });
            this.$router.push({ name: 'plans' });
          })
          .catch((errors) => {
            _.forEach(errors, (error) => {
              Alerts.danger(error[0]);
            });
          });
      }
    }
  },
  mounted: function () {
    if (this.can('read_organizations')) {
      Api.findAll('organizations').then(({ data }) => {
        this.organizations = data;
      });
    }

    this.setBreadcrumbs([{ to: { name: 'plans' }, text: 'Plan' }]);
    if (this.plan_id) {
      Api.find('plans', this.plan_id, {
        include: 'organization',
        fields: { organizations: 'id,name,is_managed' }
      }).then(({ data }) => {
        this.plan = data;
        this.scopes = data.meta.feature_scopes;
        this.is_whitelabel = data.organization.is_managed;
        this.enabledFeatures = this.plan.feature_scopes.split(' ');
      });
    } else {
      this.plan = {
        name: '',
        included_shipments: 0,
        included_order_channels: 0,
        included_users: 0,
        included_printers: 0,
        included_carriers: 0,
        included_return_portals: 0,
        included_automations: 0,
        price_shipments: 0,
        price_order_channels: 0,
        price_users: 0,
        price_printers: 0,
        price_carriers: 0,
        price_return_portals: 0,
        price_automations: 0,
        billing_period: 'monthly',
        currency: 'EUR',
        billing_product_id: null,
        payment_fee_percentage: 1,
        created_at: null,
        updated_at: null,
        price: 0,
        visible: false,
        level_one_support_price: 0,
        level_two_support_price: 0,
        level_three_support_price: 0,
        support_class: 0
      };
    }
  }
};
</script>

<style scoped></style>
